export type Legacy360Subtype = "living_trust" | "125plan" | "new_agent" | "advantage";

export const LEGACY360_PIPELINE_IDS = {
  living_trust: "AtnyZ5hxQGhpBwTO8gro",
  "125plan": "hoIcCAyZ8Wg0wPPNzosy",
  new_agent: "LHgTWf4RYWr6SZlbe94h",
  advantage: "EKY9Qps5kZDkRBbLjL4t",
} as const satisfies Record<Legacy360Subtype, string>;

export const LEGACY360_PIPELINE_SUBTYPE: Record<string, Legacy360Subtype> = Object.fromEntries(
  Object.entries(LEGACY360_PIPELINE_IDS).map(([subtype, pipelineId]) => [pipelineId, subtype]),
) as Record<string, Legacy360Subtype>;

export const LEGACY360_SUBTYPE_CONFIG: Record<
  Legacy360Subtype,
  { leadType: string; formName: string; label: string }
> = {
  living_trust: {
    leadType: "legacy360_living_trust",
    formName: "Legacy360 - Living Trust Application",
    label: "Living Trust",
  },
  "125plan": {
    leadType: "legacy360_125plan",
    formName: "Legacy360 - 125 Plan Savings Analysis",
    label: "125 Plan",
  },
  new_agent: {
    leadType: "legacy360_new_agent",
    formName: "Legacy360 - New Agent Intake",
    label: "New Agent",
  },
  advantage: {
    // Keep the external workflow's misspelling at the integration boundary.
    // Existing commission rules and stored records currently use this value.
    leadType: "lagecy360_advantage",
    formName: "Legacy360 - Advantage / Agent Expansion Intake",
    label: "Advantage / Expansion",
  },
};

export type Legacy360Identity = {
  ghlPipelineId?: string | null;
  l360LeadType?: string | null;
  l360FormName?: string | null;
  l360PageUrl?: string | null;
  l360Plan125NumEmployees?: unknown;
  l360Plan125NumW2Employees?: unknown;
  l360CurrentlyLicensed?: unknown;
  l360LicenseTypes?: unknown;
  l360TrustGoal?: unknown;
  l360PlanningType?: unknown;
};

/**
 * Resolve exactly one Legacy360 application type.
 * Pipeline ID is authoritative because one Wibiz contact can retain answers
 * from several forms. Explicit form identity comes next, then field presence.
 */
export function resolveLegacy360Subtype(input: Legacy360Identity): Legacy360Subtype | null {
  if (input.ghlPipelineId && LEGACY360_PIPELINE_SUBTYPE[input.ghlPipelineId]) {
    return LEGACY360_PIPELINE_SUBTYPE[input.ghlPipelineId];
  }

  const leadType = (input.l360LeadType ?? "").trim().toLowerCase();
  const formName = (input.l360FormName ?? "").trim().toLowerCase();
  const pageUrl = (input.l360PageUrl ?? "").trim().toLowerCase();

  // Explicit identity must win over stale fields left on the shared contact.
  // Lead type is stronger than form name, and form name is stronger than URL.
  for (const identityText of [leadType, formName, pageUrl]) {
    if (identityText.includes("125plan") || identityText.includes("125 plan") || identityText.includes("section 125")) {
      return "125plan";
    }
    if (identityText.includes("advantage") || identityText.includes("agent expansion") || identityText.includes("lagecy360_advantage")) {
      return "advantage";
    }
    if (identityText.includes("new_agent") || identityText.includes("new agent")) {
      return "new_agent";
    }
    if (identityText.includes("living_trust") || identityText.includes("living trust")) {
      return "living_trust";
    }
  }

  if (input.l360Plan125NumEmployees || input.l360Plan125NumW2Employees) return "125plan";
  if (input.l360CurrentlyLicensed || input.l360LicenseTypes) return "advantage";
  if (input.l360TrustGoal || input.l360PlanningType) return "living_trust";
  return null;
}

export function legacy360CanonicalIdentity(subtype: Legacy360Subtype) {
  const config = LEGACY360_SUBTYPE_CONFIG[subtype];
  return { l360LeadType: config.leadType, l360FormName: config.formName };
}

export const LEGACY360_LIVING_TRUST_FIELDS = [
  "l360State",
  "l360ExistingPlan",
  "l360TrustGoal",
  "l360PlanningType",
  "l360RealEstate",
  "l360AssetValue",
  "l360HasBeneficiaries",
  "l360Timeline",
  "l360PreferredContact",
  "l360QuestionsComments",
  "l360ConsentToContact",
] as const;

export const LEGACY360_ADVANTAGE_FIELDS = [
  "l360CityState",
  "l360CurrentlyLicensed",
  "l360LicenseTypes",
  "l360GoodStanding",
  "l360IndustryExperience",
  "l360CurrentRole",
  "l360ContractedWithAgency",
  "l360AgencyType",
  "l360LeadSources",
  "l360LookingFor",
  "l360LicenseRevoked",
  "l360WillingBackgroundCheck",
  "l360WillingToContract",
] as const;

export const LEGACY360_PLAN125_FIELDS = [
  "l360Plan125NumEmployees",
  "l360Plan125NumW2Employees",
  "l360Plan125PayrollProvider",
  "l360Plan125HasHealthIns",
  "l360Plan125PayrollReportUrl",
] as const;

export const LEGACY360_ALL_DATA_FIELDS = [
  "l360LeadType",
  "l360FormName",
  "l360PageTitle",
  "l360PageUrl",
  ...LEGACY360_LIVING_TRUST_FIELDS,
  ...LEGACY360_ADVANTAGE_FIELDS,
  ...LEGACY360_PLAN125_FIELDS,
] as const;

const LEGACY360_ALL_DATA_FIELD_SET = new Set<string>(LEGACY360_ALL_DATA_FIELDS);

export function isLegacy360DataField(key: string): boolean {
  return LEGACY360_ALL_DATA_FIELD_SET.has(key);
}

/** Return only fields that belong to one Legacy360 application type. */
export function pickLegacy360FieldsForSubtype(
  input: Record<string, unknown>,
  subtype: Legacy360Subtype,
  options: { includeSubmissionMetadata?: boolean } = {},
): Record<string, unknown> {
  const allowed: readonly string[] = subtype === "125plan"
    ? LEGACY360_PLAN125_FIELDS
    : subtype === "advantage"
      ? LEGACY360_ADVANTAGE_FIELDS
      : subtype === "living_trust"
        ? LEGACY360_LIVING_TRUST_FIELDS
        : [];

  const output: Record<string, unknown> = { ...legacy360CanonicalIdentity(subtype) };
  if (options.includeSubmissionMetadata) {
    if (input.l360PageTitle != null && input.l360PageTitle !== "") output.l360PageTitle = input.l360PageTitle;
    if (input.l360PageUrl != null && input.l360PageUrl !== "") output.l360PageUrl = input.l360PageUrl;
  }
  for (const key of allowed) {
    const value = input[key];
    if (value !== undefined && value !== null && value !== "") output[key] = value;
  }
  return output;
}

export function sanitizeLegacy360FieldsForSubtype<T extends Record<string, unknown>>(
  input: T,
  subtype: Legacy360Subtype,
  options: { includeSubmissionMetadata?: boolean } = {},
): T {
  const output: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (!isLegacy360DataField(key)) output[key] = value;
  }
  return {
    ...output,
    ...pickLegacy360FieldsForSubtype(input, subtype, options),
  } as T;
}

export function legacy360SubtypeLabel(input: Legacy360Identity): string {
  const subtype = resolveLegacy360Subtype(input) ?? "living_trust";
  return LEGACY360_SUBTYPE_CONFIG[subtype].label;
}

export type Legacy360Candidate = Legacy360Identity & {
  id?: number;
  verticalName?: string | null;
  ghlOpportunityId?: string | null;
  businessName?: string | null;
};

/**
 * Select a safe existing row for a subtype-specific event. Returning null is
 * intentional when more than one opportunity could own a contact-only event.
 */
export function selectLegacy360Candidate<T extends Legacy360Candidate>(
  candidates: T[],
  incomingSubtype: Legacy360Subtype,
  incomingBusinessName?: string | null,
): T | null {
  const sameSubtype = candidates.filter(candidate =>
    candidate.verticalName === "Legacy360" &&
    resolveLegacy360Subtype(candidate) === incomingSubtype,
  );
  const normalizedBusiness = incomingBusinessName?.trim().toLowerCase() || null;
  const businessMatches = normalizedBusiness
    ? sameSubtype.filter(candidate =>
        !candidate.businessName || candidate.businessName.trim().toLowerCase() === normalizedBusiness,
      )
    : sameSubtype;
  const eligible = businessMatches.length > 0 ? businessMatches : sameSubtype;

  const pipelineRows = eligible.filter(candidate => !!candidate.ghlOpportunityId);
  if (pipelineRows.length === 1) return pipelineRows[0];
  if (pipelineRows.length > 1) return null;
  if (eligible.length === 1) return eligible[0];
  if (eligible.length > 1) return null;

  const promotable = candidates.filter(candidate =>
    !candidate.ghlOpportunityId &&
    candidate.verticalName === "Legacy360" &&
    resolveLegacy360Subtype(candidate) == null,
  );
  return promotable.length === 1 ? promotable[0] : null;
}
